import { BaseLogProcessor, type ProcessorResult } from './base-log-processor';
import { type FlowLogEvent, type FlowLogFlowData, type FlowLogModuleData } from '../types/flow-log';
import { Edge, MarkerType, Node } from '@xyflow/react';

const MODULE_NODE_WIDTH = 320;
const MODULE_NODE_HEIGHT = 400;
const HORIZONTAL_SPACING = 100;

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  const layoutedNodes = nodes.map((node, index) => ({
    ...node,
    position: {
      x: index * (MODULE_NODE_WIDTH + HORIZONTAL_SPACING) + 100,
      y: MODULE_NODE_HEIGHT / 2
    }
  }));

  return { nodes: layoutedNodes, edges };
};

export class FlowLogProcessor extends BaseLogProcessor<FlowLogEvent[], FlowLogEvent> {
  private flowData: FlowLogFlowData | null = null;
  private moduleConfig: Record<string, { displayName: string; category: string }>;

  constructor() {
    super();
    this.moduleConfig = {
      SetLoggingBehavior: {
        displayName: 'Set Logging',
        category: 'configuration'
      },
      LoopPrompts: {
        displayName: 'Loop Prompts',
        category: 'configuration'
      },
      InvokeExternalResource: {
        displayName: 'Invoke External Resource',
        category: 'configuration'
      },
      CheckAttribute: {
        displayName: 'Check Attribute',
        category: 'configuration'
      },
      SetRecordingBehavior: {
        displayName: 'Set Recording',
        category: 'configuration'
      },
      SetWisdomAssistant: {
        displayName: 'Set Wisdom Assistant',
        category: 'ai'
      },
      SetContactData: {
        displayName: 'Set Contact Data',
        category: 'data'
      },
      SetVoice: {
        displayName: 'Set Voice',
        category: 'communication'
      },
      GetUserInput: {
        displayName: 'Get User Input',
        category: 'input'
      },
      SetAttributes: {
        displayName: 'Set Attributes',
        category: 'data'
      },
      PlayPrompt: {
        displayName: 'Play Prompt',
        category: 'communication'
      },
      TransferToAgent: {
        displayName: 'Transfer to Agent',
        category: 'routing'
      },
      DisconnectParticipant: {
        displayName: 'Disconnect',
        category: 'routing'
      }
    };
  }

  public clear(): void {
    this.flowData = null;
  }

  public processLogs(logData: FlowLogEvent[]): FlowLogEvent[] {
    const sortedEvents = [...logData].sort((a, b) => new Date(a.Timestamp).getTime() - new Date(b.Timestamp).getTime());

    this.clear();

    if (sortedEvents.length === 0) {
      return [];
    }

    const flowGroups = new Map<string, FlowLogEvent[]>();

    for (const event of sortedEvents) {
      const flowId = event.ContactFlowId;
      if (!flowGroups.has(flowId)) {
        flowGroups.set(flowId, []);
      }
      flowGroups.get(flowId)!.push(event);
    }

    const firstEvent = sortedEvents[0];
    const lastEvent = sortedEvents[sortedEvents.length - 1];
    const startTime = this.parseTimestamp(firstEvent.Timestamp);
    const endTime = this.parseTimestamp(lastEvent.Timestamp);

    this.flowData = {
      id: 'combined-flows',
      name: 'Flow Logs',
      contactFlowId: '',
      startTime,
      endTime,
      duration: this.calculateDuration(startTime, endTime),
      modules: []
    };

    // Track execution count for each identifier to handle loops
    const executionCountMap = new Map<string, number>();
    const modules: FlowLogModuleData[] = [];

    for (const event of sortedEvents) {
      const timestamp = this.parseTimestamp(event.Timestamp);
      const config = this.moduleConfig[event.ContactFlowModuleType] || {
        displayName: event.ContactFlowModuleType,
        category: 'general'
      };

      const isKeyValueAssignment = ['SetAttributes'].includes(event.ContactFlowModuleType);

      // Get current execution count for this identifier
      const currentCount = executionCountMap.get(event.Identifier) ?? 0;
      executionCountMap.set(event.Identifier, currentCount + 1);

      // Create unique ID by appending execution count
      const uniqueId = `${event.Identifier}-${currentCount}`;

      // Create new module for each execution
      const module: FlowLogModuleData = {
        id: uniqueId,
        type: event.ContactFlowModuleType,
        identifier: event.Identifier,
        timestamp: timestamp,
        parameters: isKeyValueAssignment && event.Parameters ? [event.Parameters] : (event.Parameters ?? {}),
        result: event.Results,
        displayName: config.displayName,
        category: config.category,
        contactFlowId: event.ContactFlowId,
        contactFlowName: event.ContactFlowName,
        executionCount: currentCount
      };
      modules.push(module);
    }

    this.flowData.modules = modules;

    return sortedEvents;
  }

  public convertToFlowData(processedLogs: FlowLogEvent[]): ProcessorResult {
    if (!this.flowData || !this.flowData.modules.length) {
      return { nodes: [], edges: [], processedLogs };
    }

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Create module nodes directly without groups
    this.flowData.modules.forEach((module, index) => {
      const nodeId = `module-${module.id}`;

      // Calculate duration based on timestamp difference with next module
      let duration = 0;
      if (index < this.flowData!.modules.length - 1) {
        const nextModule = this.flowData!.modules[index + 1];
        duration = nextModule.timestamp - module.timestamp; // Duration in milliseconds
      } else {
        // For the last module, use a default duration or calculate from flow end time
        duration = (this.flowData?.endTime ?? module.timestamp) - module.timestamp; // Duration in milliseconds
      }

      nodes.push({
        id: nodeId,
        type: 'flowBlock',
        position: { x: 0, y: 0 },
        draggable: true,
        data: {
          label: module.displayName || module.type,
          category: module.category || 'general',
          duration,
          moduleType: module.type,
          identifier: module.identifier,
          parameters: module.parameters,
          result: module.result,
          timestamp: module.timestamp,
          timestampDisplay: new Date(module.timestamp).toLocaleTimeString(),
          contactFlowName: module.contactFlowName,
          executionCount: module.executionCount
        }
      });

      // Connect to next module
      if (index < this.flowData!.modules.length - 1) {
        const nextModule = this.flowData!.modules[index + 1];
        const nextNodeId = `module-${nextModule.id}`;
        edges.push({
          id: `${nodeId}-${nextNodeId}`,
          source: nodeId,
          target: nextNodeId,
          type: 'default',
          animated: this.flowData ? this.flowData.modules.length < 100 : true,
          style: { strokeWidth: 2 },
          markerEnd: { type: MarkerType.Arrow }
        });
      }
    });

    const layoutedElements = getLayoutedElements(nodes, edges);

    return {
      nodes: layoutedElements.nodes,
      edges: layoutedElements.edges,
      processedLogs
    };
  }
}
